import { useState, useMemo } from "react";

/**
 * Standard column-sort behavior for any table in the app — see CLAUDE.md:
 * every table column gets a sort control. Click a header to sort ascending;
 * click again to flip to descending; click a different header to sort by
 * that column instead (starting ascending).
 *
 * Usage:
 *   const { sorted, sortKey, sortDir, toggleSort, sortArrow } = useSort(rows, "next_run_date");
 *   <th onClick={() => toggleSort("next_run_date")}>Next Run{sortArrow("next_run_date")}</th>
 *   {sorted.map(...)}
 */
export function useSort(rows, defaultKey = null, defaultDir = "asc") {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sorted = useMemo(() => {
    if (!sortKey || !rows) return rows || [];
    const copy = [...rows];
    copy.sort((a, b) => {
      let va = a ? a[sortKey] : null;
      let vb = b ? b[sortKey] : null;
      const aNull = va === null || va === undefined || va === "";
      const bNull = vb === null || vb === undefined || vb === "";
      if (aNull && bNull) return 0;
      if (aNull) return 1;   // nulls always sort last, regardless of direction
      if (bNull) return -1;
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  // Every sortable header needs a visible affordance — not just the active
  // one. A column with no icon looks unsortable; cursor:pointer alone is
  // invisible until the admin happens to hover over exactly that header.
  // Inactive columns get a neutral, low-emphasis "sortable" glyph; the
  // active column gets its actual direction, in the theme's accent color
  // via thSort() below.
  const SORT_NEUTRAL = " ⇅";
  const SORT_ASC = " ↑";
  const SORT_DESC = " ↓";
  const sortArrow = (key) => (sortKey === key ? (sortDir === "asc" ? SORT_ASC : SORT_DESC) : SORT_NEUTRAL);
  const thSort = (key, T) => ({
    cursor: "pointer", userSelect: "none",
    color: sortKey === key ? (T?.blue || "#034e7b") : undefined,
  });

  return { sorted, sortKey, sortDir, toggleSort, sortArrow, thSort };
}
